/**
 * Pure arithmetic for the clearance chart (`ClearanceChart.tsx`).
 *
 * It lives here, next to the other clearance lib modules, for the same reason
 * `stationVolumes/chartLayout.ts` does: the grouping and the tick maths are
 * assertable on their own, and a React test should not be the only thing
 * standing between a corridor-length run and 4 000 one-pixel `<rect>`s.
 *
 * The palette-free arithmetic (grouping, `k2Verdict`, ticks, bands, …) lives
 * in `@gis/clearance` (`libs/clearance/src/chart.ts`), shared with the report
 * worker, and is re-exported below. Only what pairs the K2 ladder with the
 * client palette or formats for the dock stays in this file.
 */
import type { ClearanceBarKind } from '@gis/clearance/chart';

import { SV_COLORS, type SvPalette } from '@/lib/stationVolumes/tokens';

export * from '@gis/clearance/chart';

/** `var(--sv-x, <the light palette's x>)` — see `CLEARANCE_FILL`. */
const swatch = (key: keyof SvPalette): string => `var(--sv-${key}, ${SV_COLORS.light[key]})`;

/**
 * The K2 ladder's ONE pairing with the palette (`SvPalette`): the chart's bars
 * and legend read it as CSS custom properties, `clearanceBandColor` reads the
 * very same palette fields as hex for the 3D envelope's per-bin bands. Two
 * renderers, one map — so a light/dark flip repaints both, and no hex is
 * forked.
 *
 * The custom property for a palette field is `--sv-<field>` by construction
 * (`tokens.ts`'s `vars`), which is what lets `CLEARANCE_FILL` below be derived
 * rather than restated.
 */
export const CLEARANCE_K2_KEY = {
  violation: 'cut',
  // `caution`, not `warn` (findings M3/M4). `warn` is documented in `tokens.ts`
  // as a STATUS ink and is spent, in this dock's very own header row, on the
  // "alignment changed" chip — one hue cannot mean both "close to the limit"
  // and "possibly the wrong stations". `caution` is the CLEARANCE ramp's own
  // boundary amber, so the near-miss bar, the near-miss plate and the band of
  // the cloud they describe are ONE colour instead of three adjacent ambers.
  nearMiss: 'caution',
  clear: 'clear',
} as const satisfies Record<ClearanceBarKind, keyof SvPalette>;

/**
 * Ruling K2, drawn: a bar is red once anything actually intruded, amber when
 * nothing did but a point sat inside the OUTERMOST near-miss band (`k2Verdict`
 * — the same rung the MARGINAL verdict turns on), and neutral otherwise. Three states rather than two, because "clear" and "cleared it by
 * 6 cm" are the two facts a clearance sweep exists to separate.
 *
 * The heights that make the amber visible live in `BAR_FLOOR_PX` below: a
 * near-miss bin plots ZERO under both metrics, so without a floor the colour
 * would be applied to a rect of zero height and never render.
 *
 * The literal fallback is the LIGHT palette's own value rather than a
 * hand-picked rgb() — the fallback only ever applies outside `.sv-root`, where
 * any value is a guess, and a guess that came from the palette cannot drift
 * from it.
 */
export const CLEARANCE_FILL = {
  violation: swatch(CLEARANCE_K2_KEY.violation),
  nearMiss: swatch(CLEARANCE_K2_KEY.nearMiss),
  clear: swatch(CLEARANCE_K2_KEY.clear),
} as const satisfies Record<ClearanceBarKind, string>;

/**
 * The K2 key's labels. Named as well as coloured: red and amber at 10 px are
 * not an identity for a reader who cannot separate the two hues. Shared with
 * the 3D bands' legend in `ClearanceDock`, so the two keys cannot disagree
 * about what the colours mean.
 */
export const CLEARANCE_LEGEND_KEYS = {
  violation: 'clearance.chart.legendViolation',
  nearMiss: 'clearance.chart.legendNearMiss',
  clear: 'clearance.chart.legendClear',
} as const satisfies Record<ClearanceBarKind, string>;

/** The order a bar escalates through the ladder — the order the key is read in. */
export const CLEARANCE_LEGEND_ORDER: ClearanceBarKind[] = ['violation', 'nearMiss', 'clear'];

/**
 * Count-axis tick text. Never a bare lowercase "m": `Intl` compact notation
 * prints 1.5 million as "1.5m" in en-GB, which in a dock where every other
 * "m" is metres says the bar is 1.5 metres tall (audit §A4, a correctness
 * bug). Below 10 000 the tick is the grouped integer; above, a scaled figure
 * with an explicit `k` / `M` suffix separated by a thin space (U+2009), the
 * decimal localised. `k`/`M` are the SI multipliers — locale-neutral on
 * purpose, which is why they are not translated.
 */
export function formatCountTick(v: number, locale?: string): string {
  const n = Math.round(v);
  const abs = Math.abs(n);
  if (abs < 10_000) return n.toLocaleString(locale);
  const [div, suffix] = abs >= 1_000_000 ? [1_000_000, 'M'] : [1_000, 'k'];
  const scaled = (n / div).toLocaleString(locale, { maximumFractionDigits: 1 });
  return `${scaled}\u2009${suffix}`;
}

/**
 * Cell opacity for a ladder share, LINEAR on a fixed 0..1 scale.
 *
 * Raw counts were the wrong quantity: on a dense FAIL run every interval held
 * ~10⁶ near-miss points, and the log ramp against the run's peak (plus its
 * 0.25 floor) painted the whole pane the same saturated amber — a strip that
 * said nothing. A count is mostly a measure of point density; the SHARE of an
 * interval's points inside the band is the clearance fact.
 *
 * Fixed 0..1 rather than 0..max-in-row, deliberately: normalising to the row's
 * peak would paint the largest cell full-strength even when it is 2 %, and
 * would make a run whose intervals all sit at the same modest share look
 * uniformly saturated again — the exact failure above. On the absolute scale
 * one shade means one share in every row, every run and at every grouping, and
 * equal shares read equally light.
 *
 * Zero (or an unscored interval) draws nothing — an empty cell must read
 * empty. A non-zero share starts at a 0.2 floor so it is VISIBLE at all: on
 * a real FAIL run the shares are 0.1–1 %, and the old 0.06 floor left three
 * beige bands nobody could see (audit §A4). The ramp above it is linear.
 *
 * `max` is the theme's ceiling (the chart passes 0.45 in dark mode, where
 * full-strength amber out-shouts the red violation bars — audit §A10).
 */
export const LADDER_FLOOR_OPACITY = 0.2;

export function ladderOpacity(share: number | null, max = 1): number {
  if (share === null || !(share > 0)) return 0;
  const floor = Math.min(LADDER_FLOOR_OPACITY, max);
  return floor + (max - floor) * Math.min(1, share);
}
